import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { ColumnDef } from '@tanstack/react-table'
import TableButtonAction from 'components/Buttons/TableButtons/TableButtonAction'
import { createTableColumnHelper } from 'components/DataExpansionComponents/Common/commonColumnHelpers'
import {
  DataTableProvider,
  useAggregationRefresh,
  useDataTableContext,
} from 'components/DataTable'
import DataTable from 'components/DataTable/DataTable/DataTable'
import { IColumnVisibility } from 'components/DataTable/types'
import { FilterChips } from 'components/Filters/components/FilterChips/FilterChips'
import {
  filterStoreRepo,
  useFilterParams,
} from 'components/Filters/FilterStore'
import { TableSearch } from 'components/Filters/TableSearch'
import { ColumnSelectorRecipient } from 'components/Modals/ColumnModal/ColumnModal'
import CampaignExportModal from 'components/Modals/ExportModal/CampaignExportModal'
import OpportunitiesTableFooterControls from 'components/Opportunities/OpportunitiesTableFooterControls'
import * as S from 'components/Tables/CommonTable.styles'
import { DateCell } from 'components/Tables/CommonTableCells/DateCell'
import { USDCell } from 'components/Tables/CommonTableCells/USDCell'
import { cn } from 'components/UI/cn'
import {
  segmentedPillGroupClass,
  segmentedPillBaseClass,
  segmentedPillActiveClass,
  segmentedPillInactiveClass,
} from 'components/FbUI/segmentedPillButton'
import { IDropdownItem } from 'components/UI/Dropdown/Dropdown'
import { Switch } from 'components/UI/Switch/Switch'
import { ALL_CAMPAIGNS_TABLE_KEY } from 'constants/tableQueryKeys'
import { usePreferences } from 'context/preferences/PreferencesContext'
import {
  PiArchive,
  PiArrowCounterClockwise,
  PiDownloadSimple,
  PiWarningCircle,
} from 'react-icons/pi'
import { useCallback, useMemo, useState } from 'react'
import { Spinner } from 'react-bootstrap'
import { ColumnsStoreProvider } from 'stores/ColumnsStore/ColumnsStoreProvider'
import { dateFormatterOrFallback, formatInteger } from 'utils/formatting'
import { useFeatureFlagService } from 'utils/useFeatureFlagService'
import { getTableQueryKey } from 'utils/getTableQueryKey'
import { useTableLinkVariant } from 'components/Tables/CommonTableCells/TableLinkStyleContext'
import {
  ActionsDropdown,
  createDeleteAction,
  createEditAction,
} from '../../components/Buttons/ActionsDropdown'
import { DataTableContainer } from '../../components/DataTable/UI'
import dialogConfirm from '../../components/dialogConfirm'
import { CampaignsCell } from '../../components/FbUI/StagePill'
import { OverflownText } from '../../components/OverflownText'
import {
  Campaign,
  WORKFLOW_STATUS,
  WORKFLOW_STATUS_LABELS,
} from '../../models/campaign'
import { toast } from 'react-toastify'
import apiService from '../../services/api'
import {
  getCampaignRefreshStatus,
  useArchiveCampaign,
  useUnarchiveCampaign,
} from './campaigns_api'
import { ArchiveCampaignModal } from './ArchiveCampaignModal'
import { usePauseCampaign, useResumeCampaign } from 'services/campaigns'
import { CAMPAIGNS_COLUMNS } from './campaignsColumns'
import { CampaignsFilterset } from './CampaignsFilterset'

const PAGE_SIZE = 100
// TODO: make configurable per company (FIR-6682)
const BOUNCE_THRESHOLD = 5

// Custom over the shared DateCell because DateCell renders new Date(null) as Dec 31, 1969.
const NullableDateCell = (info: {
  getValue: () => string | null | undefined
}) => dateFormatterOrFallback(info.getValue(), '—')

const STATUS_PILLS = [
  { label: 'All', value: null },
  { label: 'Active', value: WORKFLOW_STATUS.ACTIVE },
  { label: 'Paused', value: WORKFLOW_STATUS.PAUSED },
  { label: 'Draft', value: WORKFLOW_STATUS.DRAFT },
  { label: 'Complete', value: WORKFLOW_STATUS.COMPLETE },
  { label: 'Archived', value: 'archived' },
] as const

// DS2026 50/700 palette. Mirrors the sequence-columns + JobStatusPill pattern
// so workflow badges read as the same family across the app.
const WORKFLOW_STATUS_COLORS: Record<string, string> = {
  [WORKFLOW_STATUS.DRAFT]: 'bg-gray-100 text-gray-700',
  [WORKFLOW_STATUS.CONTACTS_REQUESTED]: 'bg-blue-50 text-blue-700',
  [WORKFLOW_STATUS.READY_TO_LAUNCH]: 'bg-indigo-50 text-indigo-700',
  [WORKFLOW_STATUS.SCHEDULED]: 'bg-indigo-50 text-indigo-700',
  [WORKFLOW_STATUS.ACTIVE]: 'bg-green-50 text-green-700',
  [WORKFLOW_STATUS.COMPLETE]: 'bg-green-50 text-green-700',
  [WORKFLOW_STATUS.PAUSED]: 'bg-amber-50 text-amber-700',
}

interface CampaignsTableProps {
  tableKey: string
  onEditCampaign?: (campaign: Campaign) => void
}

function CampaignsTableInner({
  tableKey,
  onEditCampaign,
}: CampaignsTableProps) {
  const { enableEmailSequencing } = useFeatureFlagService()
  const linkVariant = useTableLinkVariant()
  const api = apiService()
  const campaignsFilterStore = filterStoreRepo.getStore(ALL_CAMPAIGNS_TABLE_KEY)
  const { page, setPage, valueFilters } = campaignsFilterStore()
  const rawFilterParams = useFilterParams(campaignsFilterStore, PAGE_SIZE, page)

  // Convert the virtual "archived" workflow_status pill into the
  // is_archived query param the backend expects. Non-archived views
  // explicitly send is_archived=false so the table always filters,
  // while individual campaign pages (which don't pass is_archived)
  // get all campaigns including archived ones.
  const filterParams = useMemo(() => {
    const ws = rawFilterParams.workflow_status
    if (Array.isArray(ws) && ws.length === 1 && ws[0] === 'archived') {
      const params = { ...rawFilterParams }
      delete params.workflow_status
      return { ...params, is_archived: true }
    }
    return { ...rawFilterParams, is_archived: false }
  }, [rawFilterParams])

  const activeStatusFilter =
    valueFilters.workflow_status?.length === 1
      ? valueFilters.workflow_status[0].value
      : null

  function setStatusPill(value: string | null) {
    campaignsFilterStore.setState((state) => {
      if (value === null) {
        const newValueFilters = { ...state.valueFilters }
        const newPendingFilters = { ...state.pendingValueFilters }
        delete newValueFilters['workflow_status']
        delete newPendingFilters['workflow_status']
        return {
          valueFilters: newValueFilters,
          pendingValueFilters: newPendingFilters,
          page: 1,
          filter_id: undefined,
        }
      }
      const label =
        value === 'archived'
          ? 'Archived'
          : (WORKFLOW_STATUS_LABELS[value] ?? value)
      const entry = [{ value, label }]
      return {
        valueFilters: { ...state.valueFilters, workflow_status: entry },
        pendingValueFilters: {
          ...state.pendingValueFilters,
          workflow_status: entry,
        },
        page: 1,
        filter_id: undefined,
      }
    })
  }
  const abortController = new AbortController()
  const deleteCampaignMutation = api.useDeleteCampaign()
  const pauseMutation = usePauseCampaign()
  const resumeMutation = useResumeCampaign()
  const archiveMutation = useArchiveCampaign()
  const unarchiveMutation = useUnarchiveCampaign()
  const doPause = pauseMutation.mutate
  const doResume = resumeMutation.mutate
  const doUnarchive = unarchiveMutation.mutate
  const unarchiveIsPending = unarchiveMutation.isPending
  const archiveIsPending = archiveMutation.isPending
  const [pendingToggleId, setPendingToggleId] = useState<number | null>(null)
  const [showExportModal, setShowExportModal] = useState(false)
  const [archiveTarget, setArchiveTarget] = useState<Campaign | null>(null)
  const {
    state: { rowSelection, totalSelectedRows, isAllRowsSelected },
  } = useDataTableContext()

  const tableQueryKey = getTableQueryKey({
    tableKey,
    filterParams,
    page: page,
  })

  const {
    data: campaignData,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: tableQueryKey,
    queryFn: () => api.getCampaigns(abortController.signal, filterParams),
    placeholderData: keepPreviousData,
    refetchOnMount: true,
  })

  // Campaign refresh status indicator - auto-refetches table when stats refresh completes
  const { isRefreshing } = useAggregationRefresh({
    queryKey: ['campaigns-refresh-status'],
    queryFn: getCampaignRefreshStatus,
    getIsRefreshing: (data) => data.is_refreshing,
    onRefetch: useCallback(() => refetch(), [refetch]),
    refetchInterval: 5000,
  })

  const handleRowDelete = useCallback(
    async (index: number) => {
      const id = campaignData?.results[index]?.id
      if (!id) return

      if (
        await dialogConfirm(
          'Are you sure you want to delete this campaign?',
          'Delete Campaign'
        )
      ) {
        await deleteCampaignMutation.mutateAsync(id)
      }
    },
    [campaignData, deleteCampaignMutation]
  )

  const selectedCampaignIds = useMemo(() => {
    const accessor = 'id'
    return (
      campaignData?.results

        ?.filter((_: any, i: number) => rowSelection[i])

        ?.map((r: any) => {
          return accessor.split('.').reduce((acc, key) => acc[key], r)
        }) ?? []
    )
  }, [campaignData, rowSelection])

  const unselectedCampaignIds = useMemo(() => {
    if (!isAllRowsSelected) return []
    return (
      campaignData?.results
        ?.filter((_, i) => !rowSelection[i])
        ?.map((r) => r.id) ?? []
    )
  }, [campaignData, rowSelection, isAllRowsSelected])

  const { preferences } = usePreferences()
  const tooltips: Record<string, any> = preferences?.tooltips || {}
  const campaignsColumnHelper = createTableColumnHelper({
    columns: CAMPAIGNS_COLUMNS,
    tooltips,
  })

  const columns = useMemo<ColumnDef<Campaign, any>[]>(() => {
    const emailSequencingCols: ColumnDef<Campaign, any>[] =
      enableEmailSequencing
        ? [
            {
              ...campaignsColumnHelper.retrieveColumn('workflow_status'),
              id: 'workflow_status',
              accessorKey: 'workflow_status',
              size: 160,
              cell: (info) => {
                const status = info.getValue() as string | null
                if (!status) return '—'
                return (
                  <span
                    className={cn(
                      'inline-flex items-center rounded-2xl px-2.5 py-0.5 text-sm font-medium leading-5 tracking-[-0.2px]',
                      WORKFLOW_STATUS_COLORS[status] ??
                        'bg-neutral-100 text-neutral-700'
                    )}
                  >
                    {WORKFLOW_STATUS_LABELS[status] ?? status}
                  </span>
                )
              },
            },
            {
              ...campaignsColumnHelper.retrieveColumn('owner'),
              id: 'owner',
              accessorKey: 'owner_name',
              size: 200,
              cell: (info) => {
                const name = info.getValue() as string | null
                if (!name) return '—'
                const initials = name
                  .split(' ')
                  .map((n: string) => n[0])
                  .join('')
                  .toUpperCase()
                  .slice(0, 2)
                return (
                  <div className="flex items-center gap-2.5">
                    <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gray-100 text-xs font-medium leading-4 text-gray-700 tracking-[-0.2px]">
                      {initials}
                    </div>
                    <span className="truncate">{name}</span>
                  </div>
                )
              },
            },
            {
              ...campaignsColumnHelper.retrieveColumn('launch_date'),
              id: 'launch_date',
              accessorKey: 'launch_date',
              size: 130,
              cell: NullableDateCell,
            },
            {
              ...campaignsColumnHelper.retrieveColumn('actual_launch_date'),
              id: 'actual_launch_date',
              accessorKey: 'actual_launch_date',
              size: 130,
              cell: NullableDateCell,
            },
            {
              ...campaignsColumnHelper.retrieveColumn('on_off'),
              id: 'on_off',
              size: 80,
              enableSorting: false,
              meta: campaignsColumnHelper.retrieveColumn('on_off').meta,
              cell: ({ row }) => {
                const campaign = row.original
                const isActive =
                  campaign.workflow_status === WORKFLOW_STATUS.ACTIVE
                const isPaused =
                  campaign.workflow_status === WORKFLOW_STATUS.PAUSED
                if (!isActive && !isPaused) return '—'
                return (
                  <Switch
                    checked={isActive}
                    disabled={
                      pendingToggleId === campaign.bison_campaign_id ||
                      !campaign.bison_campaign_id
                    }
                    onChange={() => {
                      if (!campaign.bison_campaign_id) return
                      setPendingToggleId(campaign.bison_campaign_id)
                      const onSettled = () => setPendingToggleId(null)
                      const onError = () =>
                        toast.error(
                          `Failed to ${isActive ? 'pause' : 'resume'} campaign`
                        )
                      if (isActive) {
                        doPause(
                          {
                            bison_campaign_id: campaign.bison_campaign_id,
                            campaign_id: campaign.id,
                          },
                          {
                            onSettled,
                            onError,
                          }
                        )
                      } else {
                        doResume(
                          {
                            bison_campaign_id: campaign.bison_campaign_id,
                            campaign_id: campaign.id,
                          },
                          {
                            onSettled,
                            onError,
                          }
                        )
                      }
                    }}
                  />
                )
              },
            },
            {
              ...campaignsColumnHelper.retrieveColumn('interested_rate'),
              id: 'interested_rate',
              accessorKey: 'interested_rate',
              size: 130,
              cell: (info) => {
                const value = info.getValue() as number | null
                return value != null ? `${value}%` : '—'
              },
            },
            {
              ...campaignsColumnHelper.retrieveColumn('reply_count'),
              id: 'reply_count',
              accessorKey: 'reply_count',
              size: 100,
              cell: (info) => {
                const value = info.getValue() as number | null
                return value != null ? formatInteger(value) : '—'
              },
            },
            {
              ...campaignsColumnHelper.retrieveColumn('unread_reply_count'),
              id: 'unread_reply_count',
              accessorKey: 'unread_reply_count',
              size: 100,
              cell: (info) => {
                const value = info.getValue() as number | null
                return value != null ? formatInteger(value) : '—'
              },
            },
            {
              ...campaignsColumnHelper.retrieveColumn('action_needed'),
              id: 'action_needed',
              size: 180,
              enableSorting: false,
              meta: campaignsColumnHelper.retrieveColumn('action_needed').meta,
              cell: ({ row }) => {
                const campaign = row.original
                const status = campaign.workflow_status
                const interestedCount = campaign.action_needed_count
                const bounceCount = campaign.bounce_count

                const label =
                  status === WORKFLOW_STATUS.READY_TO_LAUNCH
                    ? 'Ready for review'
                    : interestedCount != null && interestedCount > 0
                      ? `${interestedCount} interested ${interestedCount === 1 ? 'reply' : 'replies'}`
                      : bounceCount != null && bounceCount >= BOUNCE_THRESHOLD
                        ? `${bounceCount} bounces`
                        : null

                if (!label) return '—'

                return (
                  <span className="inline-flex items-center gap-1.5 text-xs bg-wf-requested-bg text-wf-requested-text px-2.5 py-1 rounded-full whitespace-nowrap">
                    <PiWarningCircle className="size-3 shrink-0" />
                    {label}
                  </span>
                )
              },
            },
          ]
        : []

    return [
      {
        ...campaignsColumnHelper.retrieveColumn('name'),
        accessorKey: 'name',
        size: 300,
        enableHiding: false,
        meta: {
          ...campaignsColumnHelper.retrieveColumn('name').meta,
          isFirstColumn: true,
          isEditable: false,
        },
        cell: (info) => {
          return <CampaignsCell campaigns={[info.row.original]} />
        },
      },
      ...emailSequencingCols,
      // Campaign Attributes
      {
        ...campaignsColumnHelper.retrieveColumn('description'),
        accessorKey: 'description',
        size: 400,
      },
      {
        ...campaignsColumnHelper.retrieveColumn('company_type'),
        accessorKey: 'type.name',
        size: 150,
        cell: (info) => info.getValue() ?? '—',
      },
      {
        ...campaignsColumnHelper.retrieveColumn('product'),
        accessorKey: 'product.name',
        size: 250,
        cell: (info) => {
          const value = info.getValue()
          return value ? (
            <OverflownText maxLines={2}>{value}</OverflownText>
          ) : (
            'N/A'
          )
        },
      },
      {
        ...campaignsColumnHelper.retrieveColumn('created'),
        accessorKey: 'created',
        size: 200,
        cell: DateCell,
      },
      {
        ...campaignsColumnHelper.retrieveColumn('modified'),
        accessorKey: 'modified',
        size: 200,
        cell: DateCell,
      },
      {
        ...campaignsColumnHelper.retrieveColumn('days_since_last_update'),
        accessorKey: 'days_since_last_update',
        size: 200,
        cell: (info) => {
          const value = info.getValue()
          return value != null ? formatInteger(value) : '-'
        },
      },
      {
        ...campaignsColumnHelper.retrieveColumn('contact_company_count'),
        accessorKey: 'contact_company_count',
        cell: (info) => {
          return info.getValue() ? formatInteger(info.getValue()) : '-'
        },
      },
      {
        ...campaignsColumnHelper.retrieveColumn('door_count'),
        accessorKey: 'door_count',
        cell: (info) => {
          return info.getValue() ? formatInteger(info.getValue()) : '-'
        },
      },
      // Deals
      {
        ...campaignsColumnHelper.retrieveColumn('deal_count'),
        accessorKey: 'deal_count',
        cell: (info) => {
          return info.getValue() ? formatInteger(info.getValue()) : '-'
        },
      },
      {
        ...campaignsColumnHelper.retrieveColumn('deal_count_won'),
        accessorKey: 'deal_count_won',
        cell: (info) => {
          return info.getValue() ? formatInteger(info.getValue()) : '-'
        },
      },
      {
        ...campaignsColumnHelper.retrieveColumn('deal_perc'),
        accessorKey: 'deal_perc',
        cell: (info) => {
          return info.getValue() ? info.getValue() : '-'
        },
      },
      {
        ...campaignsColumnHelper.retrieveColumn('deal_perc_won'),
        accessorKey: 'deal_perc_won',
        cell: (info) => {
          return info.getValue() ? info.getValue() : '-'
        },
      },
      // Contacts
      {
        ...campaignsColumnHelper.retrieveColumn('contact_count'),
        accessorKey: 'contact_count',
        cell: (info) => {
          return info.getValue() ? formatInteger(info.getValue()) : '-'
        },
      },
      {
        ...campaignsColumnHelper.retrieveColumn('companies_with_contacts'),
        accessorKey: 'companies_with_contacts',
        cell: (info) => {
          return info.getValue() ? formatInteger(info.getValue()) : '-'
        },
      },
      {
        ...campaignsColumnHelper.retrieveColumn('companies_with_contacts_perc'),
        accessorKey: 'companies_with_contacts_perc',
        cell: (info) => {
          return info.getValue() ? info.getValue() : '-'
        },
      },
      // Forecasts
      {
        ...campaignsColumnHelper.retrieveColumn('taro'),
        accessorKey: 'taro',
        cell: USDCell,
      },
      {
        ...campaignsColumnHelper.retrieveColumn('taro_closed'),
        accessorKey: 'taro_closed',
        cell: USDCell,
      },
      {
        ...campaignsColumnHelper.retrieveColumn('est_meals_day'),
        accessorKey: 'est_meals_day',
        cell: (info) => {
          return info.getValue() ? formatInteger(info.getValue()) : '-'
        },
      },
      {
        id: 'actions',
        header: 'Actions',
        size: 80,
        enableHiding: false,
        meta: {
          isEditable: false,
          headerDisableOrdering: true,
          isLastColumn: true,
          rightAlign: true,
        },
        cell: ({ row }) => {
          const isArchived = row.original.is_archived
          const actions = [
            createEditAction(() => onEditCampaign?.(row.original)),
            {
              label: isArchived ? 'Unarchive' : 'Archive',
              icon: isArchived ? PiArrowCounterClockwise : PiArchive,
              disabled: unarchiveIsPending || archiveIsPending,
              onClick: () => {
                if (isArchived) {
                  doUnarchive(row.original.id)
                } else {
                  setArchiveTarget(row.original)
                }
              },
            },
            createDeleteAction(() => handleRowDelete(row.index)),
          ]
          return <ActionsDropdown actions={actions} containerWidth={120} />
        },
      },
    ]
  }, [
    campaignsColumnHelper,
    handleRowDelete,
    onEditCampaign,
    doPause,
    doResume,
    pendingToggleId,
    enableEmailSequencing,
    archiveIsPending,
    unarchiveIsPending,
    doUnarchive,
  ])

  const actions = useMemo(() => {
    const items: IDropdownItem[] = [
      {
        label: 'Export Selected',
        icon: <PiDownloadSimple size={18} />,
        callback: () => setShowExportModal(true),
      },
    ]

    return items
  }, [])

  return (
    <DataTableContainer key={tableKey + '-container'}>
      <DataTable
        tableHeader={
          <S.SearchContainer>
            <div
              className={cn(
                'flex w-full gap-2 items-center',
                enableEmailSequencing &&
                  '[&_[data-search-shell]]:!h-9 [&_[data-search-shell]_input]:!text-sm [&_[data-search-shell]_input]:!tracking-[-0.2px] [&_[data-table-button]]:!h-9 [&_[data-table-button]]:!min-h-9 [&_[data-table-button]_button]:!min-h-9 [&_[data-table-button]_button]:!h-9 [&_[data-table-button]_button]:!text-sm'
              )}
            >
              <TableSearch
                className="flex-1"
                filterStore={campaignsFilterStore}
                searchPlaceholder="Search by campaign name"
                tableKey={tableKey}
              />
              {enableEmailSequencing && (
                <div className={cn(segmentedPillGroupClass, 'shrink-0')}>
                  {STATUS_PILLS.map((pill) => {
                    const isActive =
                      pill.value === null
                        ? activeStatusFilter === null
                        : activeStatusFilter === pill.value
                    return (
                      <button
                        key={String(pill.value)}
                        type="button"
                        onClick={() => setStatusPill(pill.value)}
                        className={cn(
                          segmentedPillBaseClass,
                          isActive
                            ? segmentedPillActiveClass
                            : segmentedPillInactiveClass
                        )}
                      >
                        {pill.label}
                      </button>
                    )
                  })}
                </div>
              )}
              <CampaignsFilterset filterIdentifier={ALL_CAMPAIGNS_TABLE_KEY} />
              <ColumnSelectorRecipient tableKey={tableKey} />
            </div>
            <div className={cn('flex flex-row justify-between gap-4')}>
              {linkVariant !== 'plain' && (
                <TableButtonAction
                  items={actions}
                  disabled={!totalSelectedRows}
                  selectedRowsCount={totalSelectedRows ?? 0}
                />
              )}
              <FilterChips
                identifier={ALL_CAMPAIGNS_TABLE_KEY}
                store={campaignsFilterStore}
                showActive
                clearAllButton
                className="compact-filter-chips"
                excludeKeys={['workflow_status']}
              />
            </div>
          </S.SearchContainer>
        }
        selectHeaderActions={
          linkVariant === 'plain' ? (
            <TableButtonAction
              items={actions}
              disabled={!totalSelectedRows}
              selectedRowsCount={totalSelectedRows ?? 0}
            />
          ) : undefined
        }
        tableKey={tableKey}
        key={tableKey}
        stickyLastColumn={true}
        stickyFirstColumn={true}
        loading={isFetching}
        data={campaignData?.results ?? []}
        columns={columns}
        isPaginationEnabled
        paginationOptions={{
          totalRows: campaignData?.count ?? 0,
          pageSize: PAGE_SIZE,
          setPage: setPage,
          page: page,
          isPaginationLoading: isFetching,
        }}
        footerControls={
          <OpportunitiesTableFooterControls
            hideCreateCompanyButton={true}
            onDownloadButtonClick={() => setShowExportModal(true)}
          />
        }
        footerLeftControls={
          isRefreshing ? (
            <div className="flex items-center gap-2 text-sm text-fg-secondary">
              <Spinner animation="border" size="sm" />
              <span>Updating...</span>
            </div>
          ) : undefined
        }
        emptyTableText={{
          title: 'No campaigns found.',
          subtitle: 'Create a campaign or adjust filters to see results.',
        }}
      />
      <CampaignExportModal
        tableTypeSlug={'campaigns-table'}
        count={
          (showExportModal
            ? selectedCampaignIds?.length
            : campaignData?.count) ?? 0
        }
        show={showExportModal}
        _handleClose={() => {
          setShowExportModal(false)
        }}
        filters={filterParams}
        selectedIds={!isAllRowsSelected ? selectedCampaignIds : undefined}
        excludeIds={isAllRowsSelected ? unselectedCampaignIds : undefined}
      />
      <ArchiveCampaignModal
        show={!!archiveTarget}
        onHide={() => setArchiveTarget(null)}
        campaignName={archiveTarget?.name ?? ''}
        isActive={archiveTarget?.workflow_status === WORKFLOW_STATUS.ACTIVE}
        onConfirm={() => {
          if (archiveTarget) {
            archiveMutation.mutate(archiveTarget.id, {
              onSuccess: () => setArchiveTarget(null),
            })
          }
        }}
        isLoading={archiveMutation.isPending}
      />
    </DataTableContainer>
  )
}

export function CampaignsTable(
  props: CampaignsTableProps & {
    defaultColumnVisibility?: IColumnVisibility<unknown>
  }
) {
  const { enableEmailSequencing } = useFeatureFlagService()
  const { defaultColumnVisibility, ...rest } = props
  return (
    <ColumnsStoreProvider
      tableKey={props.tableKey}
      savedPresetsIdentifier={ALL_CAMPAIGNS_TABLE_KEY}
      defaultColumnVisibility={
        enableEmailSequencing
          ? {
              owner: true,
              workflow_status: true,
              on_off: true,
              launch_date: true,
              actual_launch_date: true,
              interested_rate: true,
              reply_count: true,
              unread_reply_count: true,
              action_needed: true,
              contact_company_count: true,
              description: false,
              company_type: true,
              product: false,
              created: false,
              modified: false,
              days_since_last_update: false,
              door_count: false,
              deal_count: false,
              deal_count_won: false,
              deal_perc: false,
              deal_perc_won: false,
              contact_count: false,
              companies_with_contacts: false,
              companies_with_contacts_perc: false,
              taro: false,
              taro_closed: false,
              est_meals_day: false,
              ...defaultColumnVisibility,
            }
          : defaultColumnVisibility
      }
      initialColumnOrder={
        enableEmailSequencing
          ? [
              'name',
              'company_type',
              'owner',
              'workflow_status',
              'on_off',
              'launch_date',
              'actual_launch_date',
              'contact_company_count',
              'interested_rate',
              'reply_count',
              'unread_reply_count',
              'action_needed',
            ]
          : undefined
      }
      preferredGroupOrder={
        enableEmailSequencing
          ? [
              'Email Sequencing',
              'Campaign Attributes',
              'Deals',
              'Contacts',
              'Forecasts',
            ]
          : undefined
      }
    >
      <DataTableProvider tableKey={props.tableKey}>
        <CampaignsTableInner {...rest} />
      </DataTableProvider>
    </ColumnsStoreProvider>
  )
}
